<!--
// app/components/pages/mercato/simulator/MercatoPlayerCard.vue
// @component MercatoPlayerCard - Displays player photo, name, position, market value with buy/sell buttons
-->
<script setup lang="ts">
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import { setAliasUrl } from "~~/src/Shared/utils";

type Props = {
  player: MercatoPlayer;
  mode: "buy" | "sell" | "view";
  disabled?: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  buy: [player: MercatoPlayer];
  sell: [player: MercatoPlayer];
}>();

const { $t, $tn } = useI18n();
const { playerPlaceholderPath } = useImages();

const handleBuy = () => {
  if (!props.disabled) {
    emit("buy", props.player);
  }
};

const handleSell = () => {
  emit("sell", props.player);
};

const formattedMarketValue = computed(() => {
  if (props.player.marketValue === "free") {
    return $t("mercato.free");
  }
  return `${$tn(Number(props.player.marketValue))} M`;
});
</script>

<template>
  <div class="mercato-player-card">
    <!-- Player Image -->
    <div class="mercato-player-card__image">
      <NuxtImg
        :src="setAliasUrl(player.imageUrl) || playerPlaceholderPath"
        :alt="player.name"
        width="48"
        height="48"
        sizes="48px"
        class="rounded-full object-cover object-top bg-gray-700 w-12 h-12"
        fit="outside"
      />
    </div>

    <!-- Player Info -->
    <div class="mercato-player-card__info">
      <p class="mercato-player-card__name">{{ player.name }}</p>
      <p class="mercato-player-card__position">{{ $t(player.position) }}</p>
      <p class="mercato-player-card__market-value">{{ formattedMarketValue }}</p>
    </div>

    <!-- Action Button -->
    <div class="mercato-player-card__action">
      <button
        v-if="mode === 'buy'"
        type="button"
        :disabled="disabled"
        class="btn-buy"
        :aria-label="$t('mercatoSimulator.button.buy')"
        @click="handleBuy"
      >
        {{ $t("mercatoSimulator.button.buy") }}
      </button>
      <button
        v-else-if="mode === 'sell'"
        type="button"
        class="btn-sell"
        :aria-label="$t('mercatoSimulator.button.sell')"
        @click="handleSell"
      >
        {{ $t("mercatoSimulator.button.sell") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.mercato-player-card {
  @apply bg-gray-800 rounded-lg p-3 flex items-center justify-start gap-3 border border-gray-700 transition-all duration-200;
}

.mercato-player-card__image {
  @apply flex-shrink-0;
}

.mercato-player-card__info {
  @apply flex-1 min-w-0;
}

.mercato-player-card__name {
  @apply font-bold text-xs md:text-sm mb-1;
}

.mercato-player-card__position {
  @apply text-xs text-gray-400 mb-1;
}

.mercato-player-card__market-value {
  @apply text-sm text-mercato font-semibold;
}

.mercato-player-card__action {
  @apply flex-shrink-0;
}

.btn-buy {
  @apply bg-mercato text-black px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 hover:bg-opacity-90;
}

.btn-buy:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.btn-sell {
  @apply bg-red-600 text-white px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 hover:bg-red-700;
}
</style>
